import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
const negatives = ['This is not a random collection of scuba tips.', 'This is not a beginner scuba certification course.', 'This is not a collection of disconnected exercises.', 'And it is not another resource that still leaves you with hours of preparation before you can use it.'];
const goals = ['Plan less', 'Explain skills more clearly', 'Correct students more effectively', 'Create stronger training progressions', 'Assess student development', 'Teach with more confidence and structure'];
const workflow = [{
  step: 'OPEN',
  detail: 'Open the system.'
}, {
  step: 'CHOOSE',
  detail: 'Choose the lesson or skill.'
}, {
  step: 'SEE',
  detail: 'Show the visual.'
}, {
  step: 'TEACH',
  detail: 'Run the lesson with your student.'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function SolutionReveal() {
  return <section className="py-20 px-4 bg-navy relative overflow-hidden" data-id="element-256" data-mp="2ff33xn7yegrq">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-1 bg-aqua" data-id="element-257" data-mp="2bwiayyyhm489" />

      <div className="max-w-4xl mx-auto text-center" data-id="element-258" data-mp="2gu0ol2ng97ml">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-259" data-mp="2gwwi8es9la58">
          <p className="text-sm md:text-base font-semibold text-aqua mb-4" data-id="element-260" data-mp="2pt47o6mm9n13">
            The Complete Scuba Teaching System for Instructors Worldwide
          </p>
          <h2 className="text-3xl md:text-5xl font-serif font-extrabold mb-4 text-white" data-id="element-261" data-mp="1x7m8dhf79jf1">
            SCUBA DIVING INSTRUCTOR <span className="text-cta" data-id="element-262" data-mp="224kylovqt67y">READY</span>
          </h2>
          <p className="text-base md:text-lg text-white font-semibold mb-1" data-id="element-263" data-mp="26wvwkm6xahxv">
            A Complete 6-in-1, 100% Visual Teaching System
          </p>
          <p className="text-base md:text-lg text-white/75 mb-10" data-id="element-264" data-mp="19f5kurcp0z8c">
            6 Complete Volumes · 150 Ready-to-Teach Lessons · 5 Professional Bonus Guides · 100%
            Visual
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 text-left" data-id="element-265" data-mp="2cs5npezb52ax">
          <motion.div initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="space-y-3 bg-white/5 p-6 rounded-2xl border border-white/15" data-id="element-266" data-mp="2crvw5mym6n34">
            {negatives.map(statement => <div key={statement} className="flex items-start gap-3" data-id="element-267" data-mp="1ttjn017pdd7j">
                <X className="text-red-400 shrink-0 mt-1" size={18} data-id="element-268" data-mp="1tvcs3c2o3pdf" />
                <p className="text-white/75 text-sm" data-id="element-269" data-mp="27umb0ji9cxl3">{statement}</p>
              </div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="bg-white p-6 rounded-2xl border-2 border-cta" data-id="element-270" data-mp="22w2pgk7ktnt9">
            <p className="text-navy font-medium text-base leading-relaxed mb-3" data-id="element-271" data-mp="3mokn1w3yqhmg">
              Scuba Diving Instructor Ready is a complete visual teaching system created
              specifically for scuba instructors and dive educators who want to:
            </p>
            <ul className="space-y-1.5" data-id="element-272" data-mp="3hq9cfj5ug336">
              {goals.map(goal => <li key={goal} className="flex items-start gap-3" data-id="element-273" data-mp="9bhedtuuygdv">
                  <Check className="text-ocean shrink-0 mt-0.5" size={18} data-id="element-274" data-mp="9dc21fxikmln" />
                  <span className="text-navy font-semibold text-sm" data-id="element-275" data-mp="e9sswhw3lf2f">{goal}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>

        <div className="mt-12" data-id="element-276" data-mp="4jr0xf0mjci8">
          <p className="text-white/80 text-sm font-semibold uppercase tracking-widest mb-5" data-id="element-277" data-mp="4iy16y031n9l">
            The system follows a simple workflow
          </p>
          <ol className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-0" data-id="element-278" data-mp="x1buckah4l2c">
            {workflow.map((item, index) => <li key={item.step} className="relative flex flex-col items-center px-2" data-id="element-279" data-mp="34lg78tm4jnrx" data-mpi={index}>
                <span className="text-2xl md:text-3xl font-serif font-extrabold text-cta" data-id="element-280" data-mp="30ijpf54ervp1">
                  {item.step}
                </span>
                <span className="text-sm text-white/80 mt-1" data-id="element-281" data-mp="2vlt1jvmd3vt2">{item.detail}</span>
                {index < workflow.length - 1 && <span className="hidden md:block absolute right-0 top-2 translate-x-1/2 text-2xl text-aqua/60" aria-hidden="true" data-id="element-282" data-mp="3o84c9z3ond3l">
                    →
                  </span>}
              </li>)}
          </ol>
          <p className="mt-8 text-sm md:text-base font-bold tracking-widest text-white" data-id="element-283" data-mp="39jzrmpl2kri6">
            OPEN → CHOOSE → SEE → TEACH
          </p>
        </div>
      </div>
    </section>;
}